// SkyTracker @skytracker/web — TelemetryPanel (right slide-in / mobile bottom sheet).
import { useEffect, useState } from 'react';
import type { AircraftEnrichment, PhotoResponse, Position } from '@skytracker/shared';
import { useFleet } from '../store/useFleet.js';
import { fetchAircraft, fetchPhoto } from '../lib/api.js';
import { fmtFt, fmtKt, fmtFpm, fmtHeading, fmtCoord } from '../lib/format.js';

export function TelemetryPanel(): JSX.Element {
  const selectedId = useFleet((s) => s.selectedId);
  const fleet = useFleet((s) => s.fleet);
  const setSelected = useFleet((s) => s.setSelected);
  const [enrichment, setEnrichment] = useState<AircraftEnrichment | null>(null);
  const [photo, setPhoto] = useState<PhotoResponse | null>(null);
  const position: Position | undefined = selectedId ? fleet.get(selectedId) : undefined;

  useEffect(() => {
    if (!selectedId) {
      setEnrichment(null);
      setPhoto(null);
      return;
    }
    let cancelled = false;
    void fetchAircraft(selectedId).then((data) => {
      if (cancelled) return;
      setEnrichment(data?.enrichment ?? null);
    });
    void fetchPhoto(selectedId).then((data) => {
      if (cancelled) return;
      setPhoto(data);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <aside className={`panel panel-right ${selectedId ? 'open' : ''}`}>
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>TELEMETRY</span>
        <button className="btn secondary" onClick={() => setSelected(null)} aria-label="close">×</button>
      </div>

      {!position && (
        <div className="panel-section" style={{ color: 'var(--spectre-text-dim)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
          {selectedId ? 'awaiting position…' : 'select an aircraft to view telemetry'}
        </div>
      )}

      {position && (
        <>
          <div className="panel-section">
            <div className="kv">
              <span className="k">ICAO24</span><span className="v">{position.id.toUpperCase()}</span>
              <span className="k">CALLSIGN</span><span className="v">{position.callsign ?? '—'}</span>
              <span className="k">REG</span><span className="v">{enrichment?.registration ?? position.registration ?? '—'}</span>
              <span className="k">OPERATOR</span><span className="v">{enrichment?.operator ?? '—'}</span>
              <span className="k">COUNTRY</span><span className="v">{enrichment?.country ?? '—'}</span>
              <span className="k">TYPE</span><span className="v">{enrichment?.typecode ?? '—'} {enrichment?.model ?? ''}</span>
              <span className="k">CATEGORY</span><span className="v">{position.category.toUpperCase()}</span>
              {enrichment?.is_military && <><span className="k">MIL</span><span className="v">YES</span></>}
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-header" style={{ padding: 0, marginBottom: 8, border: 0 }}>STATE</div>
            <div className="kv">
              <span className="k">LAT</span><span className="v">{fmtCoord(position.lat)}</span>
              <span className="k">LON</span><span className="v">{fmtCoord(position.lon)}</span>
              <span className="k">ALT</span><span className="v">{fmtFt(position.alt_ft)}</span>
              <span className="k">GS</span><span className="v">{fmtKt(position.gs_kt)}</span>
              <span className="k">VS</span><span className="v">{fmtFpm(position.vs_fpm)}</span>
              <span className="k">HDG</span><span className="v">{fmtHeading(position.heading_deg)}</span>
              <span className="k">SQUAWK</span>
              <span className={`v ${position.emergency ? 'emergency' : ''}`}>
                {position.squawk ?? '—'}{position.emergency ? ' EMERGENCY' : ''}
              </span>
              <span className="k">SOURCE</span><span className="v">{position.source}</span>
              <span className="k">GROUND</span><span className="v">{position.on_ground ? 'YES' : 'NO'}</span>
            </div>
          </div>

          {photo && photo.thumbnail_url && (
            <div className="panel-section">
              <div className="panel-header" style={{ padding: 0, marginBottom: 8, border: 0 }}>PHOTO</div>
              <a href={photo.link ?? '#'} target="_blank" rel="noopener noreferrer">
                <img src={photo.thumbnail_url} alt={position.id} style={{ width: '100%', display: 'block' }} />
              </a>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--spectre-text-dim)', marginTop: 4 }}>
                © {photo.photographer ?? 'unknown'} · planespotters.net
              </div>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
